import { useEffect, useState } from "react";
import { fetchMemories, fetchMemoriesTimeline, fetchChildren, fetchTimelineLanes } from "@/lib/api";
import { BookHeart, Trophy, ClipboardCheck, AlertCircle, Calendar, Mail, Loader2, Clock, GraduationCap, Activity, Stethoscope, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import useTrackPage from "@/hooks/useTrackPage";

const LANES = [
  { key: "school", label: "School", Icon: GraduationCap, tint: "bg-[var(--fcc-emma)] text-[var(--fcc-green)]" },
  { key: "sports", label: "Sports", Icon: Trophy, tint: "bg-[var(--fcc-noah)] text-amber-700" },
  { key: "activities", label: "Activities", Icon: Activity, tint: "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]" },
  { key: "medical", label: "Medical", Icon: Stethoscope, tint: "bg-rose-50 text-rose-700" },
  { key: "achievements", label: "Achievements", Icon: Sparkles, tint: "bg-purple-50 text-purple-700" },
];

const WINDOWS = [
  { k: "30d", label: "Past 30 days" },
  { k: "6mo", label: "Past 6 months" },
  { k: "year", label: "Past year" },
  { k: "all", label: "All time" },
];

function MemoriesInner() {
  useTrackPage("memories_open");
  const [window, setWindow] = useState("year");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState(null);
  const [tlLoading, setTlLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [childFilter, setChildFilter] = useState("");
  const [lanes, setLanes] = useState(null);
  const [activeLane, setActiveLane] = useState("school");

  useEffect(() => {
    setLoading(true);
    fetchMemories(window)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [window]);

  useEffect(() => {
    setTlLoading(true);
    fetchMemoriesTimeline(window, childFilter || undefined)
      .then(setTimeline)
      .catch(() => setTimeline(null))
      .finally(() => setTlLoading(false));
  }, [window, childFilter]);

  useEffect(() => {
    fetchTimelineLanes(window, childFilter || undefined)
      .then(setLanes).catch(() => setLanes(null));
  }, [window, childFilter]);

  useEffect(() => {
    fetchChildren()
      .then((rows) => setChildren((rows || []).filter((c) => !c.is_demo)))
      .catch(() => setChildren([]));
  }, []);

  const s = data?.summary || {};

  return (
    <div data-testid="memories-page" className="space-y-8">
      <header className="fcc-card p-8">
        <p className="label-overline flex items-center gap-2"><BookHeart className="w-3 h-3" /> Look back at memories</p>
        <h1 className="font-display text-4xl text-[var(--fcc-ink)] mt-3">Your family year in review</h1>
        <p className="text-[var(--fcc-ink-soft)] mt-3 max-w-2xl leading-relaxed">
          A read-only summary across your child-related emails, completed tasks, confirmed events, and weekly briefings.
          KidHQ uses this as the foundation for its long-term child growth intelligence layer.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {WINDOWS.map(w => (
            <button
              key={w.k}
              data-testid={`memories-window-${w.k}`}
              onClick={() => setWindow(w.k)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${window === w.k
                ? "bg-[var(--fcc-ink)] text-white"
                : "bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)]"}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="fcc-card p-10 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          <p className="mt-3 text-sm">Loading memories…</p>
        </div>
      ) : !data ? (
        <div className="fcc-card p-10 text-center text-[var(--fcc-muted)]">
          <p>Could not load memories. Try refreshing.</p>
        </div>
      ) : (
        <>
          <section className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={Mail} label="Child emails" value={s.child_emails ?? 0} />
            <StatCard icon={ClipboardCheck} label="Tasks completed" value={s.tasks_completed ?? 0} />
            <StatCard icon={AlertCircle} label="Tasks overdue" value={s.tasks_overdue ?? 0} accent="rose" />
            <StatCard icon={Calendar} label="Events confirmed" value={s.events_confirmed ?? 0} />
            <StatCard icon={Trophy} label="Briefings received" value={s.briefings_received ?? 0} />
          </section>

          {data.by_child?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4">By child</h2>
              <div className="grid md:grid-cols-2 gap-5">
                {data.by_child.map(c => (
                  <article key={c.child_id} data-testid={`memories-child-${c.child_id}`} className="fcc-card p-6">
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-display text-2xl text-[var(--fcc-ink)]">{c.name}</h3>
                      <span className="text-xs text-[var(--fcc-muted)]">{c.grade} · {c.school || "—"}</span>
                    </div>
                    <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">{c.email_count} child-related email{c.email_count === 1 ? "" : "s"} in this window.</p>
                    {c.top_categories?.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-2">
                        {c.top_categories.map(t => (
                          <li key={t.category} className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">
                            {t.category} · {t.count}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          <section data-testid="memories-lanes-section">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl text-[var(--fcc-ink)] flex items-center gap-2">
                  <BookHeart className="w-5 h-5 text-[var(--fcc-green)]" /> Timeline lanes
                </h2>
                <p className="text-sm text-[var(--fcc-muted)] mt-1">School · Sports · Activities · Medical · Achievements — auto-categorized from your family data.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 bg-white border border-[var(--fcc-border)] rounded-xl p-1 text-sm mb-4">
              {LANES.map((L) => {
                const count = lanes?.counts?.[L.key] ?? 0;
                return (
                  <button
                    key={L.key}
                    data-testid={`lane-tab-${L.key}`}
                    onClick={() => setActiveLane(L.key)}
                    className={`px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${activeLane === L.key ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
                  >
                    <L.Icon className="w-3.5 h-3.5" />
                    {L.label} <span className="text-[10px] opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            <LaneEvents lane={activeLane} events={lanes?.lanes?.[activeLane] || []} />
          </section>

          <section data-testid="memories-timeline-section">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl text-[var(--fcc-ink)] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[var(--fcc-green)]" /> Timeline
                </h2>
                <p className="text-sm text-[var(--fcc-muted)] mt-1">Emails, completed tasks, and confirmed events grouped by month.</p>
              </div>
              {children.length > 0 && (
                <div className="flex flex-wrap gap-1 bg-white border border-[var(--fcc-border)] rounded-xl p-1 text-sm">
                  <button
                    data-testid="timeline-child-filter-all"
                    onClick={() => setChildFilter("")}
                    className={`px-3 py-1.5 rounded-lg ${!childFilter ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
                  >
                    All family
                  </button>
                  {children.map(c => (
                    <button
                      key={c.id}
                      data-testid={`timeline-child-filter-${c.id}`}
                      onClick={() => setChildFilter(c.id)}
                      className={`px-3 py-1.5 rounded-lg ${childFilter === c.id ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {tlLoading ? (
              <div className="fcc-card p-10 text-center text-[var(--fcc-muted)]">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                <p className="mt-3 text-sm">Building timeline…</p>
              </div>
            ) : !timeline || (timeline.timeline || []).length === 0 ? (
              <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
                No timeline events yet for this window. Sync Gmail or paste a few emails to seed your history.
              </div>
            ) : (
              <div className="space-y-6">
                {timeline.timeline.map((month) => (
                  <article key={month.month} data-testid={`timeline-month-${month.month}`} className="fcc-card p-6">
                    <header className="flex items-baseline justify-between">
                      <h3 className="font-display text-xl text-[var(--fcc-ink)]">
                        {formatMonth(month.month)}
                      </h3>
                      <span className="text-xs text-[var(--fcc-muted)]">{month.events.length} event{month.events.length === 1 ? "" : "s"}</span>
                    </header>
                    <ol className="mt-4 relative border-l-2 border-[var(--fcc-border)] pl-5 space-y-4">
                      {month.events.slice(0, 25).map((ev, idx) => (
                        <li key={idx} data-testid="timeline-event" className="relative">
                          <span className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full border-2 border-white ${pinColor(ev.kind)}`} />
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="font-semibold text-sm text-[var(--fcc-ink)] truncate">{ev.title || "(no title)"}</p>
                            <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] shrink-0">{(ev.at || "").slice(0, 10)}</span>
                          </div>
                          {ev.summary && <p className="text-xs text-[var(--fcc-ink-soft)] mt-0.5">{ev.summary}</p>}
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <span className={`fcc-pill text-[10px] ${kindPillClass(ev.kind)}`}>{kindLabel(ev.kind)}</span>
                            {ev.category && <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)] text-[10px]">{ev.category}</span>}
                            {ev.child_name && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">For {ev.child_name}</span>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </article>
                ))}
              </div>
            )}
          </section>

          {data.by_category?.length > 0 && (
            <section className="fcc-card p-7">
              <h2 className="font-display text-xl text-[var(--fcc-ink)]">Activity mix</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {data.by_category.map(c => (
                  <li key={c.category} className="flex items-center justify-between">
                    <span className="text-[var(--fcc-ink-soft)]">{c.category}</span>
                    <span className="text-[var(--fcc-ink)] font-semibold">{c.count}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.highlights?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4">Recent highlights</h2>
              <ul className="space-y-3">
                {data.highlights.map((h, idx) => (
                  <li key={idx} className="fcc-card p-5">
                    <div className="flex items-baseline justify-between">
                      <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{h.category || "Other"}</span>
                      <span className="text-xs text-[var(--fcc-muted)]">{(h.received_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="font-semibold text-[var(--fcc-ink)] mt-2">{h.subject}</p>
                    <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{h.summary || h.organization}</p>
                    {h.child_name && (
                      <p className="text-xs text-[var(--fcc-muted)] mt-2">For {h.child_name}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function LaneEvents({ lane, events }) {
  if (!events.length) {
    return (
      <div className="fcc-card p-8 text-center text-sm text-[var(--fcc-muted)]" data-testid={`lane-empty-${lane}`}>
        No events in this lane yet for the selected window.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid={`lane-events-${lane}`}>
      {events.slice(0, 30).map((e, idx) => (
        <li key={idx} data-testid="lane-event-row">
          <Link
            to={e.deeplink || "#"}
            className="fcc-card p-3.5 flex items-start gap-3 hover:bg-[var(--fcc-cream-2)] transition"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-[var(--fcc-green)] mt-2 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold text-sm text-[var(--fcc-ink)] truncate">{e.title}</p>
                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] shrink-0">{(e.at || "").slice(0, 10)}</span>
              </div>
              {e.summary && <p className="text-xs text-[var(--fcc-ink-soft)] mt-0.5 truncate">{e.summary}</p>}
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{e.kind}</span>
                {e.category && <span className="fcc-pill text-[10px] bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{e.category}</span>}
                {e.child_name && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">For {e.child_name}</span>}
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const tint = accent === "rose"
    ? "bg-rose-50 text-rose-700"
    : "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]";
  return (
    <div className="fcc-card p-5">
      <span className={`inline-flex w-10 h-10 rounded-xl items-center justify-center ${tint}`}>
        <Icon className="w-4 h-4" />
      </span>
      <p className="text-3xl font-display text-[var(--fcc-ink)] mt-4">{value}</p>
      <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)] mt-1">{label}</p>
    </div>
  );
}

function formatMonth(ym) {
  if (!ym || ym === "unknown") return "Earlier";
  const [y, m] = ym.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(dt.getTime())) return ym;
  return dt.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function pinColor(kind) {
  if (kind === "email") return "bg-[var(--fcc-green)]";
  if (kind === "task") return "bg-amber-500";
  if (kind === "event") return "bg-indigo-500";
  if (kind === "milestone") return "bg-rose-500";
  return "bg-[var(--fcc-muted)]";
}

function kindLabel(kind) {
  if (kind === "email") return "Email";
  if (kind === "task") return "Task";
  if (kind === "event") return "Event";
  if (kind === "milestone") return "Milestone";
  return kind || "Other";
}

function kindPillClass(kind) {
  if (kind === "email") return "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]";
  if (kind === "task") return "bg-amber-50 text-amber-700";
  if (kind === "event") return "bg-indigo-50 text-indigo-700";
  if (kind === "milestone") return "bg-rose-50 text-rose-700";
  return "bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]";
}

export default function MemoriesPage() {
  return (
    <PageErrorBoundary scope="memories">
      <MemoriesInner />
    </PageErrorBoundary>
  );
}
